<script setup lang="ts">
import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import VCard from '@components/card/VCard/VCard.vue';
import {
  BALANCE_CORRECTIONS_PERMISSIONS,
  TRANSLATION_GROUP_BALANCE_CORRECTIONS,
} from '@modules/balance-corrections/constants';
import { useDownloadBalanceCorrectionsReportMutation } from '@modules/balance-corrections/mutations';
import { useBalanceCorrectionsListQuery } from '@modules/balance-corrections/queries';
import VDownloadReportButton from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import { useDownloadReport } from '@modules/download-report/composables';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useBalanceCorrectionsListQuery);

const {
  isReportLoading,
  onDownloadReport,
} = useDownloadReport(useDownloadBalanceCorrectionsReportMutation(), tableState);

const tableFilterOptions: ITableFilterProps = {
  pathForFilter: 'api2/admin/affiliate_cashouts/adjustment_history',
  actionForFilter: 'list',
  byMerchant: true,
  filterName: 'adjustment_history',
};

const columnOptions: ITableColumnsOptions = {
  merchant_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  created_at: {
    type: ESmartFilterColumnType.DATE,
  },
  id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  affiliate_id: {
    type: ESmartFilterColumnType.COMPARE,
    defaultMobilePreview: true,
  },
  admin_id: {
    type: ESmartFilterColumnType.COMPARE,
  },
  admin_email: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  comment: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  affiliate_email: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  affiliate_username: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  amount: {
    type: ESmartFilterColumnType.COMPARE,
  },
  affiliate_balance_before: {
    type: ESmartFilterColumnType.COMPARE,
  },
  affiliate_balance_after: {
    type: ESmartFilterColumnType.COMPARE,
  },
  payment_system_name: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
  payment_system_details: {
    type: ESmartFilterColumnType.CHECKBOX,
  },
};
</script>

<template>
  <VCard v-data-qa-auto-id="'BalanceCorrectionsListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_BALANCE_CORRECTIONS, 'HEADER')"
      :filter-props="tableFilterOptions"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template
        v-if="$hasPermission(BALANCE_CORRECTIONS_PERMISSIONS.DOWNLOAD_LIST)"
        #footerButtons
      >
        <VDownloadReportButton
          :label="$getTranslation(TRANSLATION_GROUP_BALANCE_CORRECTIONS, 'DOWNLOAD_REPORT')"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
      </template>
    </VTable>
  </VCard>
</template>
